import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSession, useCan } from './session'
import { Panel } from '@/components/erp/primitives'

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status)
  const user = useSession((s) => s.user)
  const location = useLocation()

  if (status !== 'authenticated') {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }
  // mustChangePassword is a HARD gate — the API rejects everything else with
  // 403 password-change-required, so the UI must not pretend otherwise.
  if (user?.mustChangePassword && location.pathname !== '/password-change') {
    return <Navigate to="/password-change" replace />
  }
  return <>{children}</>
}

export function RequireAnonymous({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status)
  if (status === 'authenticated') return <Navigate to="/" replace />
  return <>{children}</>
}

/** Route-level permission gate. Renders the reason, not a blank page. */
export function RequirePermission({
  permission, children,
}: { permission: string; children: ReactNode }) {
  if (useCan(permission)) return <>{children}</>
  return (
    <Panel title="Access Denied" description={`This screen requires the ${permission} permission.`}>
      <div className="border border-red-200 bg-red-50/60 rounded-sm px-4 py-3 text-erp-sm text-red-700">
        You do not have permission to view this page. Ask an administrator to grant it.
      </div>
    </Panel>
  )
}
